import { useCallback, useEffect, useMemo, useState } from 'react';

export type AdminThemePreference = 'light' | 'dark' | 'system';
export type ResolvedAdminTheme = Exclude<AdminThemePreference, 'system'>;

export const ADMIN_THEME_STORAGE_KEY = 'biteos_admin_theme';
const ADMIN_THEME_CHANGE_EVENT = 'biteos:admin-theme-change';
const ADMIN_THEME_PATHS = ['/admin', '/pos', '/call-center', '/staff-workspace'];

export const normalizeAdminThemePreference = (value: unknown): AdminThemePreference =>
  value === 'light' || value === 'dark' || value === 'system' ? value : 'system';

export const resolveAdminTheme = (
  preference: AdminThemePreference,
  systemPrefersDark: boolean,
): ResolvedAdminTheme => preference === 'system' ? (systemPrefersDark ? 'dark' : 'light') : preference;

export const isAdminThemePath = (pathname: string) =>
  ADMIN_THEME_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));

const readStoredPreference = (): AdminThemePreference => {
  if (typeof window === 'undefined') return 'system';
  return normalizeAdminThemePreference(window.localStorage.getItem(ADMIN_THEME_STORAGE_KEY));
};

const systemPrefersDark = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;

export const applyAdminThemeToDocument = (
  pathname: string,
  preference = readStoredPreference(),
  prefersDark = systemPrefersDark(),
) => {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  if (!isAdminThemePath(pathname)) {
    delete root.dataset.adminTheme;
    delete root.dataset.adminThemePreference;
    return;
  }

  root.dataset.adminTheme = resolveAdminTheme(preference, prefersDark);
  root.dataset.adminThemePreference = preference;
};

export const initializeAdminTheme = (pathname: string) => applyAdminThemeToDocument(pathname);

export function useAdminTheme() {
  const [preference, setPreferenceState] = useState<AdminThemePreference>(readStoredPreference);
  const [prefersDark, setPrefersDark] = useState(systemPrefersDark);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onMediaChange = (event: MediaQueryListEvent) => setPrefersDark(event.matches);
    const onStorage = (event: StorageEvent) => {
      if (event.key === ADMIN_THEME_STORAGE_KEY) {
        setPreferenceState(normalizeAdminThemePreference(event.newValue));
      }
    };
    const onThemeChange = (event: Event) => {
      const detail = (event as CustomEvent<AdminThemePreference>).detail;
      setPreferenceState(normalizeAdminThemePreference(detail));
    };

    media.addEventListener('change', onMediaChange);
    window.addEventListener('storage', onStorage);
    window.addEventListener(ADMIN_THEME_CHANGE_EVENT, onThemeChange);
    return () => {
      media.removeEventListener('change', onMediaChange);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(ADMIN_THEME_CHANGE_EVENT, onThemeChange);
    };
  }, []);

  const setPreference = useCallback((next: AdminThemePreference) => {
    const normalized = normalizeAdminThemePreference(next);
    window.localStorage.setItem(ADMIN_THEME_STORAGE_KEY, normalized);
    setPreferenceState(normalized);
    window.dispatchEvent(new CustomEvent(ADMIN_THEME_CHANGE_EVENT, { detail: normalized }));
  }, []);

  return useMemo(() => ({
    preference,
    resolvedTheme: resolveAdminTheme(preference, prefersDark),
    setPreference,
  }), [preference, prefersDark, setPreference]);
}
